import React, { useMemo } from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { aggErrors } from 'app-components/flux/account-discovery';
import type { AggAction } from 'app-components/flux/account-discovery/accountDiscoveryTypes';
import type { InstitutionLogin } from 'app-components/flux/institution-logins/institutionLoginsTypes';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';

import AccountManageUpdatesChip from 'components/AccountManageUpdatesChip';
import { showManageUpdatesDialog } from 'components/Dialogs/ManageUpdatesDialog/dispatcher';
import InstitutionItem from 'components/InstitutionItem';

type DialogAlert = (title: string, body: string, onClose: () => void, buttons: string[]) => void;

// Shared by AccountStatusButton and CreditCardFixIssueButton so both entry points show the
// same reconnect copy for Apple Wallet accounts, which QCS can't issue a normal agg action for.
export function showAppleWalletReconnectAlert(dialogAlert: DialogAlert): void {
  dialogAlert(
    'Apple Wallet access needed',
    'There was an issue connecting to Apple Wallet. Re-connect the account using your iPhone.',
    () => undefined,
    ['OK'],
  );
}

// Decorates the QCS agg action's confirmation dialog with the institution row and, for MFA
// statuses, a 'Manage Updates' deny button. Shared by AccountStatusButton and ReauthDialog so
// both entry points show the same dialog for the same error.
const useAggActionWithConfirmation = (
  institutionLogin: InstitutionLogin | null | undefined,
  aggStatus: string | null | undefined,
  aggStatusCode: string | null | undefined,
  featureFlags: Parameters<typeof aggErrors.getQcsAggregationAction>[3],
): AggAction | null | undefined =>
  useMemo(() => {
    const qcsAggAction = aggErrors.getQcsAggregationAction(institutionLogin, aggStatus, aggStatusCode, featureFlags);
    const actionWithConfirmation = qcsAggAction?.actionWithConfirmation;
    if (
      !qcsAggAction ||
      !actionWithConfirmation ||
      actionWithConfirmation.payload.type !== 'DIALOG_CONFIRMATION' ||
      !actionWithConfirmation.payload.props
    ) {
      return qcsAggAction;
    }

    const confirmationProps = actionWithConfirmation.payload.props;
    const manageUpdatesInstitutionLogin =
      ['MFA_CHALLENGE_READY_FOR_USER', 'MFA_REQUIRED_ACTION'].includes(aggStatus || '') &&
      qcsAggAction.institutionLogin;
    return {
      ...qcsAggAction,
      get actionWithConfirmation() {
        const content = confirmationProps.get('content');
        return rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            type: 'DIALOG_CONFIRMATION',
            allowNesting: false,
            props: confirmationProps
              .set(
                'content',
                <Box display='flex' flexDirection='column' gap={3}>
                  {typeof content === 'string' ? (
                    <Typography
                      variant='bodyRegular'
                      id='confirmation-dialog-description'
                      sx={{ whiteSpace: 'pre-line' }}
                    >
                      {content}
                    </Typography>
                  ) : (
                    content
                  )}

                  <Paper variant='outlined'>
                    <Box padding={2} gap={2} display='flex' alignItems='center' justifyContent='space-between'>
                      <InstitutionItem value={institutionLogin?.id} />
                      <AccountManageUpdatesChip open institutionLoginId={institutionLogin?.id || ''} />
                    </Box>
                  </Paper>
                </Box>,
              )
              .merge(
                // replace 'cancel' with 'manage updates' for MFA errors
                !manageUpdatesInstitutionLogin
                  ? {}
                  : {
                      denyButtonVariant: 'text',
                      denyLabel: 'Manage Updates',
                      denyAction: () => showManageUpdatesDialog({ defaultValue: manageUpdatesInstitutionLogin.id }),
                      onClose: (_event: Event, reason: string) => reason !== 'DENY',
                    },
              ),
          }),
        );
      },
    };
  }, [aggStatus, aggStatusCode, featureFlags, institutionLogin]);

export default useAggActionWithConfirmation;
